import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import { buttonStyles } from './css/commonStyles';
import {
  colors,
  newRequestBtnSx,
  pageShellStyles,
  subTabSx,
  tabHeaderRowSx,
  tableAlertErrorSx,
  tableCardStyles,
  tableContainerStyles,
  pageTitleSx,
} from './css/revenueItems.styles';
import { buildPaginationItems } from './utils/revenueItems.utils';
import { getPaymentRequests } from 'helpers/paymentRequest.api';
import config from 'config';
import { useVaultRevenueRoles } from './utils/vaultRoles.utils';

import ManagementTab from './components/ManagementTab';
import RevenueSharingSection from './components/RevenueSharingSection';
import TransparencySection from './components/TransparencySection';
import ClaimRevenueTab from './components/ClaimRevenueTab';
import CreatePaymentDialog from './components/CreatePaymentDialog';
import PaymentRequestRow from './components/PaymentRequestRow';
import PaymentsPagination from './components/PaymentsPagination';
import CopyLinkToast from './components/CopyLinkToast';
import SuccessToast from './components/SuccessToast';
import VaultNotConfiguredBanner from './components/VaultNotConfiguredBanner';

const REVENUE_TAB = {
  TRANSPARENCY: 'transparency',
  CONFIGURATION: 'configuration',
  PAYMENTS: 'payments',
  SHARING: 'sharing',
  CLAIM: 'claim',
};

const VAULT_OPS_TABS = new Set([REVENUE_TAB.PAYMENTS, REVENUE_TAB.SHARING]);

const RevenueItemsTab = ({ currentDao, onRefreshDao }) => {
  const vaultRoles = useVaultRevenueRoles(
    currentDao?.vaultAddress,
    currentDao?.chainId,
    currentDao?.organizerAddress,
  );
  const {
    canAccessOps,
    canAccessConfiguration,
    loading: rolesLoading,
    refresh: refreshRoles,
    isVaultAdmin,
    isSplitterOperator,
    isVaultOperator,
    splitterAddress,
    operatorRows,
    isSplitterAdmin,
  } = vaultRoles;

  const [activeTab, setActiveTab] = useState(REVENUE_TAB.TRANSPARENCY);

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [toast, setToast] = useState({ open: false, paymentUrl: '' });
  const [copyToast, setCopyToast] = useState(false);

  const [paymentRequests, setPaymentRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, totalItems: 0, totalPages: 1 });
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    setPage(1);
    setActiveTab(REVENUE_TAB.TRANSPARENCY);
  }, [currentDao?._id]);

  useEffect(() => {
    if (!canAccessConfiguration && activeTab === REVENUE_TAB.CONFIGURATION) {
      setActiveTab(REVENUE_TAB.TRANSPARENCY);
    }
    if (!canAccessOps && VAULT_OPS_TABS.has(activeTab)) {
      setActiveTab(REVENUE_TAB.TRANSPARENCY);
    }
  }, [canAccessConfiguration, canAccessOps, activeTab]);

  useEffect(() => {
    if (!canAccessOps || activeTab !== REVENUE_TAB.PAYMENTS || !currentDao?._id) return undefined;

    let isMounted = true;
    const fetch = async () => {
      setIsLoading(true);
      setError('');
      try {
        const res = await getPaymentRequests({ page, limit: 20, sortBy: 'desc', daoId: currentDao._id });
        if (!isMounted) return;
        setPaymentRequests(res?.data || []);
        setPagination({
          page: res?.pagination?.page || page,
          limit: res?.pagination?.limit || 20,
          totalItems: res?.pagination?.totalItems || 0,
          totalPages: res?.pagination?.totalPages || 1,
        });
      } catch (err) {
        if (!isMounted) return;
        setPaymentRequests([]);
        setPagination({ page: 1, limit: 20, totalItems: 0, totalPages: 1 });
        setError(err?.message || 'Failed to load payment requests.');
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    fetch();
    return () => {
      isMounted = false;
    };
  }, [currentDao?._id, page, refreshKey, canAccessOps, activeTab]);

  const paginationItems = useMemo(
    () => buildPaginationItems(pagination.page || 1, pagination.totalPages || 1),
    [pagination.page, pagination.totalPages],
  );

  const paginationLabel = useMemo(() => {
    if (!pagination.totalItems) return isLoading ? 'Loading payment requests...' : 'No payment requests found';
    const start = (pagination.page - 1) * pagination.limit + 1;
    const end = Math.min(start + paymentRequests.length - 1, pagination.totalItems);
    return `Showing ${start}-${end} of ${pagination.totalItems} payment requests`;
  }, [isLoading, pagination, paymentRequests.length]);

  const handleCopyPaymentUrl = (memoHash) => {
    if (!memoHash) return;
    const url = `${config.sites?.kambria || window.location.origin}/pay?memoHash=${memoHash}`;
    navigator.clipboard.writeText(url);
    setCopyToast(true);
  };

  const handleCreateSuccess = (paymentUrl) => {
    setRefreshKey((k) => k + 1);
    setToast({ open: true, paymentUrl });
  };

  const handleTabChange = (e, value) => {
    if (!canAccessOps && VAULT_OPS_TABS.has(value)) return;
    if (!canAccessConfiguration && value === REVENUE_TAB.CONFIGURATION) return;
    setActiveTab(value);
  };

  const renderTableBody = () => {
    if (isLoading) {
      return (
        <TableRow>
          <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5 }}>
              <CircularProgress size={20} sx={{ color: colors.accent }} />
              <Typography sx={{ color: colors.textSecondary }}>Loading payment requests...</Typography>
            </Box>
          </TableCell>
        </TableRow>
      );
    }
    if (paymentRequests.length) {
      return paymentRequests.map((item) => (
        <PaymentRequestRow key={item._id} item={item} onCopyPaymentUrl={handleCopyPaymentUrl} />
      ));
    }
    return (
      <TableRow>
        <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
          <Typography sx={{ color: colors.textSecondary }}>No payment requests found.</Typography>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Box sx={pageShellStyles}>
      <Tabs value={activeTab} onChange={handleTabChange} sx={subTabSx}>
        <Tab label="Transparency" value={REVENUE_TAB.TRANSPARENCY} />
        {canAccessConfiguration && <Tab label="Configuration" value={REVENUE_TAB.CONFIGURATION} />}
        {canAccessOps && <Tab label="Payment requests" value={REVENUE_TAB.PAYMENTS} />}
        {canAccessOps && <Tab label="Revenue sharing" value={REVENUE_TAB.SHARING} />}
        <Tab label="Claim revenue" value={REVENUE_TAB.CLAIM} />
      </Tabs>

      {!rolesLoading && !canAccessOps && !canAccessConfiguration && activeTab !== REVENUE_TAB.TRANSPARENCY && activeTab !== REVENUE_TAB.CLAIM && (
        <Typography sx={{ color: colors.textSecondary, mb: 2, fontFamily: 'Questrial, sans-serif' }}>
          Connect a wallet with Vault DAO_OPERATOR, or the DAO organizer wallet for Configuration only.
        </Typography>
      )}

      {activeTab === REVENUE_TAB.TRANSPARENCY && <TransparencySection currentDao={currentDao} />}

      {activeTab === REVENUE_TAB.CONFIGURATION && canAccessConfiguration && (
        <ManagementTab
          currentDao={currentDao}
          onSaveSuccess={onRefreshDao}
          isVaultAdmin={isVaultAdmin}
          isOrganizer={vaultRoles.isOrganizer}
          hasOnChainAccess={vaultRoles.hasOnChainAccess}
          hasVault={vaultRoles.hasVault}
          isSplitterOperator={isSplitterOperator}
          isVaultOperator={isVaultOperator}
          splitterAddress={splitterAddress}
          operatorRows={operatorRows}
          isSplitterAdmin={isSplitterAdmin}
          onRolesRefresh={refreshRoles}
          rolesLoading={rolesLoading}
        />
      )}

      {activeTab === REVENUE_TAB.SHARING && canAccessOps && (
        <RevenueSharingSection
          currentDao={currentDao}
          vaultAddress={currentDao?.vaultAddress}
          onGoToConfiguration={() => setActiveTab(REVENUE_TAB.CONFIGURATION)}
        />
      )}

      {activeTab === REVENUE_TAB.CLAIM && <ClaimRevenueTab currentDao={currentDao} />}

      {activeTab === REVENUE_TAB.PAYMENTS && canAccessOps && (
        <>
          <Box sx={tabHeaderRowSx}>
            <Typography variant="h6" sx={pageTitleSx}>
              Payment requests
            </Typography>
            <Button
              variant="contained"
              onClick={() => setIsCreateDialogOpen(true)}
              disabled={!currentDao?.vaultAddress}
              sx={{ ...buttonStyles, ...newRequestBtnSx }}
            >
              New Request
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={tableAlertErrorSx}>
              {error}
            </Alert>
          )}

          {!currentDao?.vaultAddress && (
            <VaultNotConfiguredBanner
              message="Vault address is not configured. Please set it up before creating payment requests."
              actionLabel={canAccessConfiguration ? 'Update Vault Address' : undefined}
              onAction={canAccessConfiguration ? () => setActiveTab(REVENUE_TAB.CONFIGURATION) : undefined}
            />
          )}

          <Box sx={tableCardStyles}>
            <TableContainer component={Box} sx={tableContainerStyles}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 56 }}>Payment link</TableCell>
                    <TableCell sx={{ minWidth: 170 }}>Revenue Type</TableCell>
                    <TableCell sx={{ minWidth: 150 }}>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell sx={{ minWidth: 170 }}>Memo Hash</TableCell>
                    <TableCell sx={{ minWidth: 150 }}>External Ref</TableCell>
                    <TableCell sx={{ minWidth: 150 }}>Created Date</TableCell>
                    <TableCell sx={{ minWidth: 150 }}>Expired At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>{renderTableBody()}</TableBody>
              </Table>
            </TableContainer>

            <PaymentsPagination
              pagination={pagination}
              paginationItems={paginationItems}
              paginationLabel={paginationLabel}
              onPageChange={setPage}
            />
          </Box>

          <CreatePaymentDialog
            open={isCreateDialogOpen}
            onClose={() => setIsCreateDialogOpen(false)}
            currentDao={currentDao}
            onSuccess={handleCreateSuccess}
          />

          <CopyLinkToast open={copyToast} onClose={() => setCopyToast(false)} />

          <SuccessToast
            open={toast.open}
            paymentUrl={toast.paymentUrl}
            onClose={() => setToast({ open: false, paymentUrl: '' })}
          />
        </>
      )}
    </Box>
  );
};

export default RevenueItemsTab;
